import { isHomeHealthExternalUser } from "../constants/sso";

/** A clinic that belongs to the Compassus tenant. */
export const isCompassusClinic = (
  clinic?: { clinic_type?: string | null } | null
): boolean => clinic?.clinic_type === "compassus";

/** A Compassus palliative program/branch (clinic name contains "palliative"). */
export const isCompassusPalliativeClinic = (
  clinic?: { clinic_type?: string | null; name?: string | null } | null
): boolean =>
  isCompassusClinic(clinic) && /palliative/i.test(clinic?.name ?? "");

type CompassusUserLike = {
  groups?: unknown;
  email?: string | null;
};

/**
 * Whether the current session should render the Compassus experience.
 *
 * The authenticated user's selected clinic is authoritative on shared CIP
 * hosts. Hostname/SSO/group/email-based external users continue to receive the
 * same experience even before clinic data has loaded.
 */
export const isCompassusExperience = ({
  user,
  userClinic,
}: {
  user?: CompassusUserLike | null;
  userClinic?: { clinic_type?: string | null } | null;
}): boolean =>
  isHomeHealthExternalUser(
    (user?.groups as Parameters<typeof isHomeHealthExternalUser>[0]) ?? null,
    user?.email ?? null
  ) || isCompassusClinic(userClinic);

/**
 * Whether the current user is a Compassus user. True when they are a
 * home-health-external (Compassus) SSO user, or any of their clinics is a
 * Compassus clinic. Used to gate Compassus-only modules (e.g. Productivity).
 */
export const isCompassusUser = ({
  user,
  userClinic,
  clinicList,
}: {
  user?: CompassusUserLike | null;
  userClinic?: { clinic_type?: string | null } | null;
  clinicList?: Array<{ clinic_type?: string | null }> | null;
}): boolean =>
  isCompassusExperience({ user, userClinic }) ||
  (clinicList ?? []).some((clinic) => isCompassusClinic(clinic));
